import { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import moment from 'moment';
import { useDataContext } from 'store';

export const useCallbackRef = (callback) => {
    const callbackRef = useRef(callback);
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);
    return callbackRef;
};

export const useEventListener = (eventName, callback, ref, options) => {
    const callbackRef = useCallbackRef(callback);
    useEffect(() => {
        const targetEl = ref?.current ?? window;
        const listener = (e) => callbackRef.current?.(e);
        targetEl?.addEventListener?.(eventName, listener, options);
        return () => {
            targetEl?.removeEventListener?.(eventName, listener, options);
        };
    }, [eventName, ref, options, callbackRef]);
};

export const useInterval = (callback, delay) => {
    const id = useRef();
    const callbackRef = useCallbackRef(callback);
    const [count, setCount] = useState(0);
    useEffect(() => {
        id.current = setTimeout(async () => {
            await callbackRef.current?.();
            setCount((prev) => prev + 1);
        }, delay);
        return () => clearTimeout(id.current);
    }, [delay, count, callbackRef]);
    const stop = useCallback(() => {
        clearTimeout(id.current);
    }, []);
    const restart = useCallback(() => {
        setCount((prev) => prev + 1);
    }, []);
    return { stop, restart };
};

export const useElementSize = (ref) => {
    const [size, setSize] = useState({ width: 0, height: 0 });
    const setElemenetSize = useCallback(() => {
        if (ref?.current) {
            const { offsetWidth = 0, offsetHeight = 0 } = ref.current;
            if (size.width !== offsetWidth || size.height !== offsetHeight) {
                setSize({ width: offsetWidth, height: offsetHeight });
            }
        }
    }, [ref, size]);
    useEffect(() => setElemenetSize());
    useEventListener('resize', setElemenetSize);
    return size;
};

export const useFormatData = ({ columns, data }) => {
    const dataSource = useDataContext();
    const cols = useMemo(() => (
        columns?.reduce((prev, col) => ({
            ...prev,
            [col.dataIndex]: col
        }), {}) || {}
    ), [columns]);
    const result = useMemo(() => (
        data?.map((item) => {
            const newItem = Object.entries(item).reduce((prev, curr) => {
                const [key, value] = curr;
                const { type, options, multiple, format, defaultValue } = cols[key] || {};
                switch (type) {
                    case 'Number':
                    case 'NumberFloat':
                        prev[key] = value || value === 0 ? Number(value) : '';
                        break;
                    case 'Select': {
                        const currOptions = typeof options === 'string' ? dataSource[options] : options;
                        if (!multiple) {
                            const findOption = currOptions?.find((option) => String(option.value) === String(value));
                            prev[key] = findOption?.label || '';
                        } else {
                            const stringValue = value?.map((v) => String(v));
                            const findOptions = currOptions?.filter((option) => (
                                stringValue?.includes(String(option.value))
                            ));
                            prev[key] = findOptions?.map((option) => option.label).join(', ') || '';
                        }
                        break;
                    }
                    case 'Date':
                        prev[key] = value ? moment(value).format('YYYY/MM/DD') : '';
                        break;
                    case 'DateTime':
                        prev[key] = value ? moment(value).format('YYYY/MM/DD HH:mm') : '';
                        break;
                    case 'Time':
                        prev[key] = value ? moment(value).format('HH:mm') : '';
                        break;
                    default:
                        prev[key] = value ?? defaultValue ?? '';
                }
                if (format) {
                    prev[key] = format(prev[key]);
                }
                return prev;
            }, {});
            return newItem;
        }) || []
    ), [cols, data, dataSource]);
    return result;
};

export const useSearchData = ({ columns, data, filterValues, searchText }) => {
    const keys = useMemo(() => (
        columns?.map((col) => col.dataIndex) || []
    ), [columns]);
    const result = useMemo(() => (
        data?.filter((item) => {
            let match = true;
            if (filterValues) {
                const entries = Object.entries(filterValues);
                for (let i = 0; i < entries.length; i += 1) {
                    const [key, value] = entries[i];
                    if (Reflect.has(item, key) && value && item[key] !== value) {
                        match = false;
                    }
                }
            }
            if (searchText && match) {
                match = false;
                const text = searchText.toUpperCase();
                const entries = Object.entries(item);
                for (let i = 0; i < entries.length; i += 1) {
                    const [key, value] = entries[i];
                    if (keys.includes(key)) {
                        const stringValue = String(value).toUpperCase();
                        if (stringValue.includes(text)) {
                            match = true;
                            break;
                        }
                    }
                }
            }
            return match;
        }) || []
    ), [keys, data, filterValues, searchText]);
    return result;
};
